// @flow

import React, { Component } from "react";
import { get, isEqual, noop, sortBy } from "lodash";
import type {
  PastPerformanceRace as PastPerformanceRaceType,
  RaceCardBettingInterest,
  RaceProgram,
  RaceTypeCodeEnum,
  Stat,
  MTPNotificationConfigType
} from "@tvg/types/Race";
import { formatPurse } from "@tvg/formatter/currency";
import { getRunner } from "@tvg/utils/getRaceAtiveRunner";
import type { TernaryFn, NullaryFn, UnaryFn } from "@tvg/types/Functional";
import { replaceCAPIVariables } from "@tvg/utils/capiUtils";
import {
  emptyMessagesDefault,
  mtpNotificationConfigDefault,
  handicappingRunnerFlagsDefault
} from "../../__mocks__/ipp-pastRaces.mock";
import HorseSwitcher from "../../_organism/HorseSwitcher";
import PastPerformanceList from "../../_organism/PastPerformanceList";
import RunnerRaceChanges, {
  hasChangesToShow,
  getSurfaceChangesKeys
} from "../../_organism/RunnerRaceChanges";
import PastPerformanceStatus from "../../_atom/PastPerformanceStatus";
import Toggle from "../../_atom/Toggle";
import SimpleInlineInfo from "../../_atom/SimpleInlineInfo";
import HorseOddsPP from "../../_molecule/HorseOddsPP";
import RunnerInfo from "../../_molecule/RunnerInfo";
import Info from "../../_molecule/Info";
import MTPNotification from "../../_molecule/MTPNotification";
import type { HandicappingRunnerFlags } from "../../_molecule/RunnerFlags";
import { infoOutline } from "../../_static/Icons/icons";

import {
  ListHolder,
  ListWrapper,
  MatchRaces,
  MatchRacesTitle,
  MatchRacesInfoContainer,
  CardWrapper,
  EmptyStateWrapper
} from "./styled-components";
import { IppMatchConditionsMask } from "../../_static/Masks";

export type EmptyMessageType = {
  emptyMessageTitle: string,
  emptyMessageNone: string,
  emptyMessageWithFilterMultiple: string,
  emptyMessageWithFilterSingle: string
};

type Props = {
  races: PastPerformanceRaceType[],
  selectedRace: number,
  totalRaces: number,
  matchQuantity: number,
  runnerStatsTitle: string,
  runnerStats: Stat[],
  onRace: UnaryFn<number, void>,
  onModalClose: NullaryFn<void>,
  onMatchToggle: NullaryFn<void>,
  isMatchConditionsOn: boolean,
  emptyMessage: string,
  borderless: boolean,
  isMobile: boolean,
  runners: RaceCardBettingInterest[],
  raceTypeCode: RaceTypeCodeEnum,
  activeRunner: string,
  activeRunnerBI: ?RaceCardBettingInterest,
  onRunnerClick: TernaryFn<string, string, number, void>,
  isLoadingRunnerPP: boolean,
  isLoadingStats: boolean,
  postRaceReportShown: boolean,
  currentRace: RaceProgram,
  noRunnerCommentMessage: string,
  pastPerformanceMatchText: string,
  emptyMessages: EmptyMessageType,
  isSilkShown: boolean,
  mtpNotificationConfig: MTPNotificationConfigType,
  handicappingRunnerFlags: HandicappingRunnerFlags,
  handicappingMTPNotification: boolean,
  isHorseSwitcherDisabled: boolean
};

const computeInfoMessage = (
  totalRaces,
  emptyMessageNone,
  emptyMessageWithFilterMultiple,
  emptyMessageWithFilterSingle
) => {
  if (totalRaces > 1) return `${totalRaces} ${emptyMessageWithFilterMultiple}`;
  if (totalRaces === 1) return emptyMessageWithFilterSingle;
  return emptyMessageNone;
};

export const renderEmpty = (
  emptyMessages: EmptyMessageType,
  totalRaces: number,
  isMatchConditionsOn: boolean,
  onMatchToggle: NullaryFn<void>,
  isMobile: boolean = false
) => {
  const {
    emptyMessageNone,
    emptyMessageWithFilterSingle,
    emptyMessageWithFilterMultiple,
    emptyMessageTitle
  } = emptyMessages;
  const computedMessage = computeInfoMessage(
    totalRaces,
    emptyMessageNone,
    emptyMessageWithFilterMultiple,
    emptyMessageWithFilterSingle
  );

  return (
    <EmptyStateWrapper isMobile={isMobile}>
      <Info
        mainIcon={infoOutline}
        hasOverlayIcon={false}
        title={emptyMessageTitle}
        message={isMatchConditionsOn ? computedMessage : emptyMessageNone}
        hasButton={isMatchConditionsOn && totalRaces > 0}
        buttonText="Turn filter off"
        buttonHandler={onMatchToggle}
        full
      />
    </EmptyStateWrapper>
  );
};

export default class RunnersPastPerformance extends Component<Props> {
  static defaultProps = {
    races: [],
    selectedRace: -1,
    totalRaces: 0,
    matchQuantity: 0,
    runnerStatsTitle: "Past Performances",
    runnerStats: [],
    onRace: noop,
    onModalClose: noop,
    onMatchToggle: noop,
    isMatchConditionsOn: false,
    emptyMessage: "No replays are available for this horse.",
    borderless: false,
    isMobile: false,
    runners: [],
    raceTypeCode: "T",
    activeRunner: "",
    activeRunnerBI: null,
    onRunnerClick: noop,
    isLoadingRunnerPP: true,
    isLoadingStats: true,
    postRaceReportShown: false,
    currentRace: {},
    noRunnerCommentMessage: "",
    pastPerformanceMatchText: "",
    isSilkShown: true,
    emptyMessages: emptyMessagesDefault,
    handicappingRunnerFlags: handicappingRunnerFlagsDefault,
    mtpNotificationConfig: mtpNotificationConfigDefault,
    handicappingMTPNotification: false,
    isHorseSwitcherDisabled: false
  };

  shouldComponentUpdate(nextProps: Props) {
    return (
      this.props.isMatchConditionsOn !== nextProps.isMatchConditionsOn ||
      this.props.totalRaces !== nextProps.totalRaces ||
      this.props.matchQuantity !== nextProps.matchQuantity ||
      this.props.activeRunner !== nextProps.activeRunner ||
      this.props.selectedRace !== nextProps.selectedRace ||
      this.props.races.length !== nextProps.races.length ||
      this.props.isLoadingRunnerPP !== nextProps.isLoadingRunnerPP ||
      this.props.isLoadingStats !== nextProps.isLoadingStats ||
      this.props.isSilkShown !== nextProps.isSilkShown ||
      this.props.isHorseSwitcherDisabled !==
        nextProps.isHorseSwitcherDisabled ||
      !isEqual(this.props.activeRunnerBI, nextProps.activeRunnerBI) ||
      !isEqual(this.props.runnerStats, nextProps.runnerStats) ||
      !isEqual(this.props.races, nextProps.races) ||
      !isEqual(this.props.currentRace, nextProps.currentRace)
    );
  }

  buildRunners = (runners: RaceCardBettingInterest[]) => {
    const newRunners = [];
    if (runners.length > 0) {
      runners.forEach((bi) =>
        bi.runners.forEach((runner) =>
          newRunners.push({ ...bi, runners: [runner] })
        )
      );
    }
    return sortBy(newRunners, ["runners[0].scratched", "biNumber"]);
  };

  renderList = () =>
    this.props.isLoadingRunnerPP || this.props.races.length > 0 ? (
      <ListWrapper
        borderless={this.props.borderless}
        data-qa-label="ipp-fulllist"
      >
        <PastPerformanceList
          raceDistance={get(this.props.currentRace, "distance") || "-"}
          raceSurface={get(this.props.currentRace, "surface.name") || "-"}
          isLoading={this.props.isLoadingRunnerPP}
          races={this.props.races}
          selectedRace={this.props.selectedRace}
          onRace={this.props.onRace}
          isMobile={this.props.isMobile}
          postRaceReportShown={this.props.postRaceReportShown}
          newHandicappingPastPerformance
        />
      </ListWrapper>
    ) : (
      this.props.isMobile &&
      renderEmpty(
        this.props.emptyMessages,
        this.props.totalRaces,
        this.props.isMatchConditionsOn,
        this.props.onMatchToggle,
        this.props.isMobile
      )
    );

  renderMatchToggle = () => {
    const pastPerformanceMatchText = replaceCAPIVariables(
      this.props.pastPerformanceMatchText,
      {
        matchQuantity: String(this.props.matchQuantity),
        totalRaces: String(this.props.totalRaces)
      }
    );

    return this.props.isLoadingRunnerPP ? (
      <IppMatchConditionsMask />
    ) : (
      (this.props.races.length > 0 || this.props.isMatchConditionsOn) && (
        <MatchRaces>
          <MatchRacesInfoContainer>
            <MatchRacesTitle
              dangerouslySetInnerHTML={{ __html: pastPerformanceMatchText }}
            />
            <SimpleInlineInfo
              info={[
                {
                  key: "numRunners",
                  value: `${get(this.props.currentRace, "numRunners")} runners`
                },
                {
                  key: "distance",
                  value: `${get(this.props.currentRace, "distance") || "-"}`
                },
                {
                  key: "course",
                  value: `${get(this.props.currentRace, "surface.name") || "-"}`
                },
                {
                  key: "condition",
                  value: `${
                    get(this.props.currentRace, "surface.defaultCondition") ||
                    "-"
                  }`
                },
                {
                  key: "raceClass",
                  value: `${
                    get(this.props.currentRace, "raceClass.name") || "-"
                  }`
                },
                {
                  key: "purse",
                  value: formatPurse(get(this.props.currentRace, "purse"))
                }
              ]}
              changed={
                this.props.currentRace.changes
                  ? getSurfaceChangesKeys(this.props.currentRace.changes)
                  : []
              }
              useUpdatedIcon
            />
          </MatchRacesInfoContainer>
          <Toggle
            on={this.props.isMatchConditionsOn}
            onClick={this.props.onMatchToggle}
            qaLabel="ipp-match-condition-button"
          />
        </MatchRaces>
      )
    );
  };

  renderHorseSwitcher = () =>
    this.props.isMobile && (
      <HorseSwitcher
        isDisabled={this.props.isHorseSwitcherDisabled}
        raceTypeCode={this.props.raceTypeCode}
        runners={this.buildRunners(this.props.runners)}
        activeRunner={this.props.activeRunner}
        onRunnerClick={this.props.onRunnerClick}
      />
    );

  renderHorseOdds = () =>
    this.props.activeRunnerBI && (
      <HorseOddsPP
        activeRunnerBI={this.props.activeRunnerBI}
        activeRunner={this.props.activeRunner}
        noRunnerCommentMessage={this.props.noRunnerCommentMessage}
        isSilkShown={this.props.isSilkShown}
      />
    );

  renderRunnerInfo = () => {
    const runner = getRunner(
      this.props.activeRunnerBI,
      this.props.activeRunner
    );
    const raceTypeCode = get(
      this.props.races,
      `[${this.props.selectedRace}].raceTypeCode`,
      "T"
    );

    return runner ? (
      <CardWrapper>
        <RunnerInfo
          title={`${this.props.activeRunner}'s Info`}
          runner={runner}
          raceTypeCode={raceTypeCode}
          handicappingRunnerFlags={this.props.handicappingRunnerFlags}
        >
          {this.renderHorseOdds()}
        </RunnerInfo>
      </CardWrapper>
    ) : null;
  };

  renderRunnerRaceChanges = () => {
    const runnerId = get(
      getRunner(this.props.activeRunnerBI, this.props.activeRunner),
      "runnerId"
    );

    // Only runner changes should be shown
    const changes = {
      horse: get(this.props.currentRace, "changes.horse"),
      surface: {}
    };
    return runnerId && changes && hasChangesToShow(changes, runnerId) ? (
      <CardWrapper>
        <RunnerRaceChanges changes={changes} runnerId={runnerId} />
      </CardWrapper>
    ) : null;
  };

  renderHeader = () =>
    (this.props.isLoadingStats ||
      this.props.races.length > 0 ||
      this.props.isMatchConditionsOn) && (
      <CardWrapper hasSeparator>
        <PastPerformanceStatus
          isLoading={this.props.isLoadingStats}
          title={this.props.runnerStatsTitle}
          races={get(this.props.runnerStats, "starts", 0)}
          wins={get(this.props.runnerStats, "wins", 0)}
          top3={get(this.props.runnerStats, "top3", 0)}
        />
      </CardWrapper>
    );

  renderMTPNotification = () =>
    this.props.handicappingMTPNotification && (
      <MTPNotification
        onModalClose={this.props.onModalClose}
        currentRace={this.props.currentRace}
        mtpNotificationConfig={this.props.mtpNotificationConfig}
      />
    );

  render() {
    return (
      <ListHolder>
        {this.renderHorseSwitcher()}
        {this.renderRunnerInfo()}
        {this.renderRunnerRaceChanges()}
        {this.renderHeader()}
        {this.renderMatchToggle()}
        {this.renderList()}
        {this.renderMTPNotification()}
      </ListHolder>
    );
  }
}
